import { motion } from "framer-motion";
import { Github, Linkedin, MapPin, ArrowDown, Download } from "lucide-react";
import { HeroScene } from "../components/three/HeroScene";
import { MaskedLines, FadeIn } from "../components/Reveal";
import { scrollToSection } from "../components/Navbar";
import { LINKS } from "../data/portfolio";

export default function Hero() {
  return (
    <section
      id="home"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
      aria-label="Hero"
    >
      <HeroScene />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/40 via-transparent to-[#0D0D0D]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-28 md:px-12">
        <FadeIn delay={0.1} className="mb-6 flex items-center gap-3">
          <span
            data-testid="hero-location-badge"
            className="glass-panel flex items-center gap-2 rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-[#F4EFEA]/80"
          >
            <MapPin size={12} className="text-[#00F2FE]" />
            Kolkata, West Bengal, India
          </span>
        </FadeIn>

        <MaskedLines
          lines={["Prithwish Sahoo"]}
          as="h1"
          data-testid="hero-headline"
          className="font-display text-[clamp(1.8rem,4.9vw,5.2rem)] font-extrabold uppercase leading-[0.95] tracking-tighter text-[#FDFBF7] sm:whitespace-nowrap"
        />

        <FadeIn delay={0.5} className="mt-8 max-w-2xl">
          <p className="font-mono text-sm uppercase tracking-[0.15em] text-[#00F2FE] md:text-base">
            Frontend &amp; Full-Stack Web Developer · AI-Integrated Web Experiences · Three.js Creative Developer
          </p>
          <p className="mt-5 text-base leading-relaxed text-[#F4EFEA]/70 md:text-lg">
            Results-driven CS Engineering student who builds AI-integrated web apps, RAG agents, and
            interactive 3D experiences — ranked Top 3000 of 86,000+ in the Amazon ML Challenge and a
            multi-time hackathon winner.
          </p>
        </FadeIn>

        <FadeIn delay={0.7} className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={() => scrollToSection("projects")}
            data-testid="hero-view-projects-btn"
            className="rounded-full bg-[#FF5E36] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#0D0D0D] transition-colors duration-300 hover:bg-[#00F2FE]"
          >
            View Projects
          </button>
          <a
            href={LINKS.resume}
            download="Prithwish_Sahoo_Resume.pdf"
            data-testid="hero-resume-download-btn"
            className="flex items-center gap-2 rounded-full border border-[#00F2FE] px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00F2FE] transition-colors duration-300 hover:bg-[#00F2FE] hover:text-[#0D0D0D]"
          >
            <Download size={14} /> Download Resume
          </a>
          <button
            onClick={() => scrollToSection("contact")}
            data-testid="hero-contact-btn"
            className="rounded-full border border-[#FDFBF7]/30 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#FDFBF7] transition-colors duration-300 hover:border-[#00F2FE] hover:text-[#00F2FE]"
          >
            Get in Touch
          </button>
          <div className="flex items-center gap-3">
            <a
              href={LINKS.github}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub profile"
              data-testid="hero-github-link"
              className="glass-panel flex h-12 w-12 items-center justify-center rounded-full text-[#F4EFEA]/80 transition-colors duration-300 hover:text-[#00F2FE]"
            >
              <Github size={18} />
            </a>
            <a
              href={LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn profile"
              data-testid="hero-linkedin-link"
              className="glass-panel flex h-12 w-12 items-center justify-center rounded-full text-[#F4EFEA]/80 transition-colors duration-300 hover:text-[#00F2FE]"
            >
              <Linkedin size={18} />
            </a>
          </div>
        </FadeIn>
      </div>

      <motion.button
        onClick={() => scrollToSection("about")}
        aria-label="Scroll to about section"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[#4A5568] transition-colors duration-300 hover:text-[#00F2FE]"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <ArrowDown size={22} />
      </motion.button>
    </section>
  );
}
